<template>
  <section class="page">
    <div class="eyebrow">🔬 鉴别诊断 · 推理链可视化</div>
    <div class="heading">
      <div>
        <h1>鉴别诊断 · <em>从病例到决策支持</em></h1>
        <p>输入病例信息，AI 同时输出雷达评分、诊断分支树与建议检查，让推理过程可被看见</p>
      </div>
    </div>

    <div class="dx-form">
      <input v-model="complaint" class="ai-input" placeholder="主诉 + 症状，如：胸痛伴大汗 30 分钟" :disabled="loading">
      <input v-model="history" class="ai-input" placeholder="既往史（选填），如：高血压 10 年、吸烟" :disabled="loading">
      <input v-model="labs" class="ai-input" placeholder="已知检查结果（选填），如：肌钙蛋白升高" :disabled="loading">
      <button class="btn" :disabled="loading || !complaint.trim()" @click="analyze">{{ loading ? '分析中…' : '🔬 推理分析' }}</button>
    </div>
    <example-chips :examples="examples" @select="complaint = $event" />

    <gate-banner :error="gateError" />

    <empty-state
      v-if="!result && !loading && !error"
      icon="🔬"
      title="推理链将在这里展开"
      description="输入病例信息后，AI 将输出雷达评分、诊断分支树（概率/支持点/不支持点/下一步检查）与建议检查清单…"
    />

    <ai-output-card v-else title="🔬 推理结果" :loading="loading" :error="error" :model="result && result.model" :mock="result && result.mock" :warning="result && result.warning">
      <template v-if="parsed">
        <div class="dx-grid">
          <div>
            <div class="section-title">诊断分支树</div>
            <branch-tree :branches="parsed.branches || []" />
            <div class="section-title" style="margin-top:18px">🚩 危险信号</div>
            <p v-for="(r, i) in parsed.redFlags || []" :key="i" class="red-flag">⚠ {{ r }}</p>
          </div>
          <div>
            <div class="section-title">推理质量评分</div>
            <score-radar :data="parsed.radar || []" />
            <div class="section-title" style="margin-top:18px">🧪 建议检查（按优先级）</div>
            <div v-for="(w, i) in parsed.workup || []" :key="i" class="workup-item"><span class="rank-badge">{{ i + 1 }}</span>{{ w }}</div>
          </div>
        </div>
      </template>
      <template v-else-if="result && result.content"><p style="white-space: pre-wrap">{{ result.content }}</p></template>
    </ai-output-card>
  </section>
</template>

<script>
import { callAI } from '../api';
import AIOutputCard from '../components/AIOutputCard.vue';
import GateBanner from '../components/GateBanner.vue';
import EmptyState from '../components/EmptyState.vue';
import ExampleChips from '../components/ExampleChips.vue';
import ScoreRadar from '../components/ScoreRadar.vue';
import BranchTree from '../components/BranchTree.vue';

export default {
  name: 'DxChain',
  components: { AIOutputCard, GateBanner, EmptyState, ExampleChips, ScoreRadar, BranchTree },
  data: () => ({
    complaint: '',
    history: '',
    labs: '',
    examples: ['胸痛伴大汗 30 分钟', '转移性右下腹痛 6 小时', '突发剧烈头痛伴呕吐', '呼吸困难伴咯血'],
    loading: false,
    error: '',
    gateError: null,
    result: null,
  }),
  computed: {
    parsed() { return this.result && this.result.parsed; },
  },
  methods: {
    async analyze() {
      if (!this.complaint.trim() || this.loading) return;
      this.loading = true;
      this.error = '';
      try {
        this.result = await callAI('dx-chain', {
          complaint: this.complaint.trim(),
          history: this.history.trim(),
          labs: this.labs.trim(),
        });
      } catch (e) {
        this.gateError = { code: e.code, msg: e.msg || e.message };
        this.error = this.gateError.msg;
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<style scoped>
.dx-form { display: flex; gap: 10px; margin-top: 18px; flex-wrap: wrap; }
.dx-form .ai-input { flex: 1; min-width: 200px; }
.dx-grid { display: grid; grid-template-columns: minmax(0, 1.6fr) minmax(0, 1fr); gap: 20px; }
.red-flag { color: var(--danger); font-size: 12px; margin: 6px 0; }
.workup-item { font-size: 13px; color: var(--text-2); padding: 8px 0; border-bottom: 1px dashed var(--border-1); }
.rank-badge { display: inline-grid; place-items: center; width: 18px; height: 18px; background: var(--primary-a15); color: var(--accent); border-radius: 5px; font-size: 11px; margin-right: 8px; }
@media (max-width: 800px) { .dx-grid { grid-template-columns: 1fr; } }
</style>
